import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { useState, useRef } from 'react';

import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { IExternalPicture } from 'shared/types/ExternalPicture';
import { Image } from 'shared/components/Image';
import { useBodyScrollLock } from 'shared/hooks/useBodyScrollLock';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { Video } from 'shared/components/Video';

interface IVideoItem {
  webmUrl?: string;
  movUrl?: string;
  mainPictureUrl?: string;
}

interface IGalleryItemMobile {
  idx?: number;
  item?: IExternalPicture | IVideoItem | { mainPictureUrl: string; altText?: string };
  itemList?:
    | IExternalPicture[]
    | { mainPictureUrl: string; altText?: string }[]
    | IBuyBarProductVariant[];
}

const ProductTemplateImagesExpandedViewMobile = dynamic(
  () => import('productTemplate/components/imageGallery/ExpandedViewMobile'),
  { ssr: false },
);

export const GalleryItemMobile = ({ item, itemList, idx }: IGalleryItemMobile) => {
  const {
    name,
    color: productColor,
    productType,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const shouldCropImage = !(productType === 'tops' && productColor.toLowerCase() === 'white');
  const [isExpanded, setIsExpanded] = useState(false);
  const [clickTargetIdx, setClickTargetIdx] = useState<number>(0);
  const isVideo = 'movUrl' in item || 'webmUrl' in item;
  const isImage = 'mainPictureUrl' in item && !isVideo;
  const isFirstChild = idx === 0;
  const ref = useRef(null);

  const handleClick = (shouldExpand) => {
    setClickTargetIdx(idx);
    setIsExpanded(shouldExpand);
  };

  const close = () => setIsExpanded(false);

  useBodyScrollLock(isExpanded, ref);

  return (
    <GalleryItemMobile.Wrapper ref={ref}>
      {isImage && (
        <Image
          alt={'altText' in item ? item.altText : name}
          crop={shouldCropImage}
          data-qa="gallery_item_mobile"
          disableLazyLoad
          fetchpriority={isFirstChild ? 'high' : 'low'}
          loading={isFirstChild ? 'eager' : 'lazy'}
          onClick={() => handleClick(true)}
          placeholderStyles={GalleryItemMobile.placeholderStyles}
          sizes="50vw"
          src={item?.mainPictureUrl || ''}
        />
      )}
      {isVideo && (
        <GalleryItemMobile.Video
          movUrl={item.movUrl}
          webmUrl={item.webmUrl}
          poster={item.mainPictureUrl}
          onClick={() => handleClick(true)}
        />
      )}
      {isExpanded && (
        <ProductTemplateImagesExpandedViewMobile
          clickTargetIdx={clickTargetIdx}
          itemList={itemList}
          name={name}
          shouldCropImage={shouldCropImage}
          onClose={close}
        />
      )}
    </GalleryItemMobile.Wrapper>
  );
};

GalleryItemMobile.Wrapper = styled.div`
  width: 100vw;
  padding: 56px 48px 45px;
  height: 100%;

  > img,
  video {
    max-height: 260px;

    @media (max-height: 540px) {
      max-height: 200px;
    }

    @media (max-width: 550px) {
      max-height: 200px;
    }
    object-fit: contain;
    height: inherit;

    :hover {
      cursor: pointer;
    }
  }
`;

GalleryItemMobile.placeholderStyles = css`
  background-position: bottom 0px left 50% !important;
  background-size: 64px 64px !important;
`;

GalleryItemMobile.Video = styled(Video)`
  display: flex;
  margin: auto;
`;
